import { RefSelect, useIdFromRouteOrNull } from '@fl/cmsch-fe-library';
import React, { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CorrectionReportDetail } from 'api/gen/CorrectionReportDetail';
import * as routerActions from 'app/router';
import { TFunction, useOurTranslation } from 'app/translations';
import { ConnectedScreenHeader } from 'common/layout';
import { formHelpers } from 'utils/forms';
import { replaceUrlWithoutRerender } from 'utils/replace-url-without-rerender';
import { apcReportFormName, ApcReportFormValues, initialApcReportFormValues } from '../components/apc-report-form/form-values';
import { analysisProtocolCorrectionAction, analysisProtocolCorrectionLoadingSelector, simpleAnalysisProtocolCorrectionSelector } from '../model';
import { convertCorrectionReportToFormValues } from '../utils/convert-correction-report-to-form-values';

/** Public screen-level setup contract returned by {@link useNewApcReportScreenSetup}. */
interface UseNewApcReportScreenSetup {
  /** Screen header node rendered above the form. */
  header: ReactNode;
  /** Shared common translation helper. */
  tCommon: TFunction<'common'>;
  /** Whether correction-report detail data is still loading. */
  correctionReportDetailLoading: boolean;
  /** Ear-tag input ref used for focus orchestration. */
  earTagRef: RefObject<HTMLInputElement>;
  /** Technician number input ref used for focus orchestration. */
  technicianNumberRef: RefObject<HTMLInputElement>;
  /** Correction-type select ref used for focus orchestration. */
  correctionTypeRef: RefSelect;
  /** Reports created during the current create/edit session. */
  createdCorrectionReports: Array<CorrectionReportDetail>;
  /** Navigates back to the overview list and resets the form state. */
  goBackToList(): void;
  /** Creates or updates a correction report from form values. */
  createCorrectionReport(values: ApcReportFormValues): void;
  /** Cancels editing and returns the form to create mode. */
  onCancel(): void;
  /** Deletes a created APC report from the session table and backend. */
  deleteCreatedApcReport(id: number, isCreatedCorrectionReport: boolean): void;
  /** Loads a created APC report back into the form for editing. */
  editCreatedApcReport(row: CorrectionReportDetail): void;
  /** Clears the locally accumulated created reports. */
  resetCreatedApcReports(): void;
}

/**
 * Creates the screen-level state and callbacks for the APC create/edit screen.
 *
 * @returns Header content, refs, created-report state, and action callbacks for the APC screen.
 *
 * @example
 * const {
 *   createCorrectionReport,
 *   createdCorrectionReports,
 *   goBackToList,
 * } = useNewApcReportScreenSetup();
 */
export const useNewApcReportScreenSetup = (): UseNewApcReportScreenSetup => {
  const {
    t,
    tCommon
  } = useOurTranslation('analysisProtocolCorrection');
  const dispatch = useDispatch();
  const apcReportId = useIdFromRouteOrNull();
  const earTagRef = useRef<HTMLInputElement>(null);
  const technicianNumberRef = useRef<HTMLInputElement>(null);
  const correctionTypeRef: RefSelect = useRef(null);
  const focusTechnicianNumber = useCallback(() => {
    setTimeout(() => technicianNumberRef.current?.focus());
  }, []);
  useEffect(() => {
    // TODO temporarily disabled due to bad data in the database.
    //  Tag TECHNICIANS (https://redmine.favorlogic.com/issues/13226)
    // dispatch(analysisProtocolCorrectionAction.getTechnicianOptions());
    if (apcReportId) {
      dispatch(analysisProtocolCorrectionAction.getCorrectionReportDetail(focusTechnicianNumber, apcReportId));
    }
    return () => {
      dispatch(analysisProtocolCorrectionAction.resetFormData());
      dispatch(formHelpers.initialize(apcReportFormName, initialApcReportFormValues));
    };
  }, [apcReportId, dispatch, focusTechnicianNumber]);
  const header = useMemo(() => <ConnectedScreenHeader title={t('analysisProtocolCorrectionReport')} />, [t]);
  const correctionType = useSelector(simpleAnalysisProtocolCorrectionSelector.correctionType);
  const createdCorrectionReports = useSelector(simpleAnalysisProtocolCorrectionSelector.createdCorrectionReports);
  const editingCorrectionReportId = useSelector(simpleAnalysisProtocolCorrectionSelector.editingCorrectionReportId);
  const {
    correctionReportDetailLoading
  } = useSelector(analysisProtocolCorrectionLoadingSelector);
  const focusEarTag = useCallback(() => {
    setTimeout(() => earTagRef.current?.focus());
  }, []);
  const createCorrectionReport = useCallback((values: ApcReportFormValues) => {
    dispatch(analysisProtocolCorrectionAction.createCorrectionReport(values, focusEarTag, editingCorrectionReportId));
  }, [dispatch, focusEarTag, editingCorrectionReportId]);
  const editCreatedApcReport = useCallback((row: CorrectionReportDetail) => {
    dispatch(analysisProtocolCorrectionAction.setEditingCorrectionReportId(row.id));
    dispatch(analysisProtocolCorrectionAction.getStableDateRelations(focusTechnicianNumber, row.earTag, correctionType));
    dispatch(formHelpers.initialize(apcReportFormName, convertCorrectionReportToFormValues(row)));
  }, [correctionType, dispatch, focusTechnicianNumber]);
  const deleteCreatedApcReport = useCallback((id: number, isCreatedCorrectionReport: boolean) => {
    dispatch(analysisProtocolCorrectionAction.deleteCorrectionReport(id, isCreatedCorrectionReport));
  }, [dispatch]);
  const resetCreatedApcReports = useCallback(() => {
    dispatch(analysisProtocolCorrectionAction.resetCreatedApcReports());
  }, [dispatch]);
  const resetForm = useCallback(() => {
    dispatch(formHelpers.initialize(apcReportFormName, initialApcReportFormValues));
    dispatch(analysisProtocolCorrectionAction.resetFormData());
  }, [dispatch]);
  const goBackToList = useCallback(() => {
    dispatch(routerActions.push('/analysis-protocol-correction'));
    resetForm();
  }, [dispatch, resetForm]);
  const onCancel = useCallback(() => {
    resetForm();
    replaceUrlWithoutRerender('/analysis-protocol-correction/new');
    setTimeout(() => technicianNumberRef.current?.focus());
  }, [resetForm]);
  return {
    header,
    tCommon,
    correctionReportDetailLoading,
    earTagRef,
    technicianNumberRef,
    correctionTypeRef,
    createdCorrectionReports,
    goBackToList,
    createCorrectionReport,
    onCancel,
    deleteCreatedApcReport,
    editCreatedApcReport,
    resetCreatedApcReports
  };
};